/**
 * Picker for `metadata_json.notify_authors` — the list of agent session
 * names that receive `chunk_committed` IACP pushes when this post is
 * edited (collab_text or any post with collab_enabled).
 *
 * UX:
 *   - Closed state: a single chip "Notify: <names>" or "Notify: off"
 *     in the editor's meta-row. Click to open.
 *   - Open state: a dropdown that lists every agent reachable via
 *     cloud-api's registry, grouped by node, with a checkbox each.
 *     Live edits debounce a few hundred ms before PATCHing the post.
 *
 * Persistence path: `apiClient.updatePost(postId, { metadata_json:
 * { ...existing, notify_authors: [...] }})`. We merge with the existing
 * metadata_json so other doctype-specific fields aren't clobbered.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { CONTENT_API } from '../config'
import { getAccessToken } from '../auth'
import type { Post } from '../types'
import { colorFromId } from './colorFromId'

interface AgentEntry {
  name: string
  node: string
  owner_email?: string
  tenant_id?: string
  is_self_owned?: boolean
  is_public?: boolean
}

interface Props {
  post: Post
}

/**
 * Issue #1969: Ein Ausfall der Agentenliste darf nicht wie "es gibt keine
 * Agenten" aussehen. content-api antwortet bei unbekanntem Inventar mit 503
 * (agent_inventory_unavailable) statt mit einer leeren Liste; hier wird das
 * als eigener Zustand gefuehrt, damit "unbekannt" von "leer" unterscheidbar
 * bleibt.
 */
const AGENTS_UNAVAILABLE = 'unavailable' as const
type AgentsState = AgentEntry[] | typeof AGENTS_UNAVAILABLE | null

async function fetchAgents(): Promise<AgentEntry[] | typeof AGENTS_UNAVAILABLE> {
  const token = getAccessToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  try {
    const r = await fetch(`${CONTENT_API}/api/v1/agents/available`, { headers })
    if (!r.ok) return AGENTS_UNAVAILABLE
    const data = (await r.json()) as { agents?: AgentEntry[] }
    return data.agents || []
  } catch {
    return AGENTS_UNAVAILABLE
  }
}

export function NotifyAuthorsPicker({ post }: Props) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [agents, setAgents] = useState<AgentsState>(null)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState('')
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const filterInputRef = useRef<HTMLInputElement>(null)

  // Current subscription list from the post's metadata.
  const subscribed = useMemo<string[]>(() => {
    const m = post.metadata_json as Record<string, unknown> | null
    const raw = m?.notify_authors
    if (!Array.isArray(raw)) return []
    return raw.filter((x): x is string => typeof x === 'string')
  }, [post.metadata_json])

  // Whether the notify-channel is enabled at all. Independent of the
  // subscriber list — `notify_enabled: true` with an empty list means
  // "ready to push, but nobody is listening yet". Default to true if
  // metadata hasn't set it explicitly AND there's at least one
  // subscriber (back-compat for posts created before this flag).
  const notifyEnabled = useMemo<boolean>(() => {
    const m = post.metadata_json as Record<string, unknown> | null
    const raw = m?.notify_enabled
    if (typeof raw === 'boolean') return raw
    return subscribed.length > 0
  }, [post.metadata_json, subscribed])

  // Fetch the agent registry once on first open, cache thereafter.
  useEffect(() => {
    if (!open || agents !== null) return
    fetchAgents().then(setAgents)
  }, [open, agents])

  // Close-on-outside-click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  // Open ↔ filter lifecycle: focus the filter input as the dropdown
  // opens so the user can type immediately; reset the filter when the
  // dropdown closes so reopening doesn't carry stale state.
  useEffect(() => {
    if (open) {
      // Wait one tick — the input only exists after agents finish loading.
      window.setTimeout(() => filterInputRef.current?.focus(), 50)
    } else {
      setFilter('')
    }
  }, [open])

  const saveMeta = async (patch: Record<string, unknown>) => {
    setSaving(true)
    try {
      const existing = (post.metadata_json as Record<string, unknown>) || {}
      const merged = { ...existing, ...patch }
      await apiClient.updatePost(post.id, { metadata_json: merged })
      queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
    } finally {
      setSaving(false)
    }
  }

  const saveSubscribed = (next: string[]) => saveMeta({ notify_authors: next })

  const toggleAgent = (name: string) => {
    const next = subscribed.includes(name)
      ? subscribed.filter(x => x !== name)
      : [...subscribed, name]
    void saveSubscribed(next)
  }

  const clearAll = () => {
    void saveSubscribed([])
  }

  // Top-level on/off toggle. Independent of the subscriber list.
  const toggleNotifyEnabled = () => {
    void saveMeta({ notify_enabled: !notifyEnabled })
  }

  // Group agents by node for the dropdown list. When a filter is set,
  // every whitespace-separated word must occur in the agent name
  // (case-insensitive AND) — typing "ark cl" finds ArkturianClaude
  // but not OnealServ. Subscribed agents are always shown, even when
  // they wouldn't match the filter, so the user can untick them.
  const grouped = useMemo(() => {
    // #1969: 'unavailable' ist kein Array — ohne diese Pruefung wuerde die
    // Schleife unten ueber die Zeichen des Wortes laufen.
    if (!Array.isArray(agents)) return null
    const words = filter
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0)
    const subscribedSet = new Set(subscribed)
    const matches = (name: string) => {
      if (subscribedSet.has(name)) return true
      if (words.length === 0) return true
      const n = name.toLowerCase()
      return words.every((w) => n.includes(w))
    }
    const byNode = new Map<string, AgentEntry[]>()
    for (const a of agents) {
      if (!matches(a.name)) continue
      if (!byNode.has(a.node)) byNode.set(a.node, [])
      byNode.get(a.node)!.push(a)
    }
    return Array.from(byNode.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [agents, filter, subscribed])

  // Render the closed-state summary. When at least one agent is
  // subscribed AND notify is enabled, show their colored dots inline
  // (max 4 dots, "+N" if more) so the user can see at a glance WHO
  // is on the list — not just a count.
  const renderSummary = () => {
    if (!notifyEnabled) return <span style={{ opacity: 0.7 }}>off</span>
    if (subscribed.length === 0) return <span style={{ opacity: 0.7 }}>on</span>
    const visible = subscribed.slice(0, 4)
    const overflow = subscribed.length - visible.length
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {visible.map(name => (
          <span
            key={name}
            title={name}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: colorFromId(`agent:${name}`, name),
                flexShrink: 0,
              }}
            />
          </span>
        ))}
        {overflow > 0 && (
          <span style={{ opacity: 0.7, fontSize: '0.9em' }}>+{overflow}</span>
        )}
      </span>
    )
  }

  return (
    <span className="notify-authors" ref={wrapperRef}>
      <span
        className={`meta-chip ${notifyEnabled ? 'meta-chip--notify-on' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={notifyEnabled
          ? (subscribed.length > 0
              ? `Notify on — pushing to: ${subscribed.join(', ')}`
              : 'Notify on — no specific agents picked yet')
          : 'Notify off — click to enable and pick agents'}
        style={{ cursor: 'pointer', opacity: saving ? 0.5 : 1 }}
      >
        <span className="label">Notify</span>{' '}
        {renderSummary()}
      </span>

      {open && (
        <div className="notify-authors-dropdown">
          <div className="notify-authors-header">
            <label className="notify-authors-master">
              <input
                type="checkbox"
                checked={notifyEnabled}
                onChange={toggleNotifyEnabled}
                disabled={saving}
              />
              <span>Notify subscribed agents on commits</span>
            </label>
            {subscribed.length > 0 && (
              <button
                className="notify-authors-clear"
                onClick={clearAll}
                title="Remove all subscriptions"
              >
                clear list
              </button>
            )}
          </div>
          {Array.isArray(agents) && agents.length > 0 && (
            <div className="notify-authors-filter">
              <input
                ref={filterInputRef}
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtern …"
                aria-label="Agents filtern"
              />
              {filter && (
                <button
                  type="button"
                  className="notify-authors-filter-clear"
                  onClick={() => setFilter('')}
                  aria-label="Filter zurücksetzen"
                >
                  ×
                </button>
              )}
            </div>
          )}
          {agents === null && (
            <div className="notify-authors-loading">Loading agents…</div>
          )}
          {agents === AGENTS_UNAVAILABLE && (
            <div className="notify-authors-empty">
              Agentenliste nicht verfügbar — die Liste ist unbekannt, nicht leer.
              Bestehende Abos bleiben unberührt.
            </div>
          )}
          {Array.isArray(agents) && agents.length === 0 && (
            <div className="notify-authors-empty">Keine Agenten vorhanden.</div>
          )}
          {Array.isArray(agents) && grouped && grouped.length === 0 && (
            <div className="notify-authors-empty">
              Kein Agent matched „{filter}".
            </div>
          )}
          {grouped && grouped.map(([node, list]) => (
            <div key={node} className="notify-authors-group">
              <div className="notify-authors-node">{node}</div>
              {list.map(a => (
                <label
                  key={a.name}
                  className={`notify-authors-item ${a.is_self_owned ? 'is-self' : ''}`}
                  title={a.owner_email ? `Owner: ${a.owner_email}` : undefined}
                >
                  <input
                    type="checkbox"
                    checked={subscribed.includes(a.name)}
                    onChange={() => toggleAgent(a.name)}
                    disabled={saving}
                  />
                  <span>{a.name}</span>
                  {a.is_self_owned && (
                    <span className="notify-authors-self-hint">·</span>
                  )}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </span>
  )
}
